/**
 * Catálogo dimensional NPS / DN / Diâmetro Externo.
 *
 * Serve para (a) exibir NPS e DN juntos quando o cadastro só tem um deles e
 * (b) preencher o diâmetro externo quando a linha não o tem cadastrado —
 * sempre identificando a origem. NPS e DN NUNCA são usados como se fossem o
 * diâmetro externo: o que entra na fórmula é o OD em mm.
 */

export interface PipeDimensionRow {
  /** NPS em polegadas, como se escreve no cadastro ("6", "1.1/2"). */
  nps: string;
  /** DN correspondente. */
  dn: number;
  /** Diâmetro externo em mm. */
  outsideDiameterMm: number;
}

export const PIPE_DIMENSION_SOURCE = "Tabela dimensional ASME B36.10M / B36.19M";

export const PIPE_DIMENSIONS: readonly PipeDimensionRow[] = [
  { nps: "1/2", dn: 15, outsideDiameterMm: 21.3 },
  { nps: "3/4", dn: 20, outsideDiameterMm: 26.7 },
  { nps: "1", dn: 25, outsideDiameterMm: 33.4 },
  { nps: "1.1/4", dn: 32, outsideDiameterMm: 42.2 },
  { nps: "1.1/2", dn: 40, outsideDiameterMm: 48.3 },
  { nps: "2", dn: 50, outsideDiameterMm: 60.3 },
  { nps: "2.1/2", dn: 65, outsideDiameterMm: 73.0 },
  { nps: "3", dn: 80, outsideDiameterMm: 88.9 },
  { nps: "4", dn: 100, outsideDiameterMm: 114.3 },
  { nps: "5", dn: 125, outsideDiameterMm: 141.3 },
  { nps: "6", dn: 150, outsideDiameterMm: 168.3 },
  { nps: "8", dn: 200, outsideDiameterMm: 219.1 },
  { nps: "10", dn: 250, outsideDiameterMm: 273.0 },
  { nps: "12", dn: 300, outsideDiameterMm: 323.8 },
  { nps: "14", dn: 350, outsideDiameterMm: 355.6 },
  { nps: "16", dn: 400, outsideDiameterMm: 406.4 },
  { nps: "18", dn: 450, outsideDiameterMm: 457.2 },
  { nps: "20", dn: 500, outsideDiameterMm: 508.0 },
  { nps: "24", dn: 600, outsideDiameterMm: 610.0 }
];

/** Normaliza "6\"", "DN 150", "6 pol" para a chave do catálogo. */
function normalizeNps(value: string | null | undefined): string {
  return String(value ?? "")
    .replace(/["']|POL(EGADAS?)?|IN(CH(ES)?)?/gi, "")
    .replace(/\s+/g, "")
    .replace(/^NPS/i, "")
    .trim();
}

export function findPipeDimensionByNps(nps: string | null | undefined): PipeDimensionRow | null {
  const wanted = normalizeNps(nps);
  if (!wanted) return null;
  return PIPE_DIMENSIONS.find(row => row.nps === wanted) ?? null;
}

export function findPipeDimensionByDn(dn: string | number | null | undefined): PipeDimensionRow | null {
  const raw = String(dn ?? "").replace(/DN/i, "").trim();
  const value = Number(raw.replace(",", "."));
  if (!Number.isFinite(value)) return null;
  return PIPE_DIMENSIONS.find(row => row.dn === value) ?? null;
}
